/* eslint-disable react/no-array-index-key */
import React from 'react';

import { capitalize, Dialog, Grid } from '@mui/material';

import * as TStyle from 'commons/components/Table/Table.styles';
import { IDeliveryNote, IDeliveryNoteInvoiceInfo } from 'commons/types';
import { RowTitleProps } from 'commons/types/listPageData.interface';
import { formatCurrency } from 'commons/utils/formatCurrency';
import { formatDate } from 'commons/utils/formatDate';
import { DataTable } from 'pages/dashboard/commons/components';
import {
  DELIVERY_NOTE_STATUS_BADGE_COLORS,
  DELIVERY_NOTE_STATUS_DISPLAY,
} from 'pages/delivery-note/dn-list/DNListPage.constants';
import { getDeliveryType } from 'pages/delivery-note/utils';

import * as S from './DeliveryNoteDetailDialog.styles';

const rowTitles: RowTitleProps[] = [
  { title: 'PO Number' },
  { title: 'Invoice Number' },
  { title: 'Picking Status' },
  { title: 'Document Type' },
  { title: 'Invoice Amount' },
  { title: 'Koli Count' },
];

const InvoiceRow: React.FC<{
  data: IDeliveryNoteInvoiceInfo;
  index: number;
}> = ({ data, index }) => (
  <TStyle.TableRow>
    <TStyle.TableColumn data-testid={`DeliveryNoteDetailPONumber-${index + 1}`}>
      {data.po_number}
    </TStyle.TableColumn>
    <TStyle.TableColumn
      data-testid={`DeliveryNoteDetailInvoiceNumber-${index + 1}`}
    >
      {data.invoice_number?.replace(/ /g, '') ?? '-'}
    </TStyle.TableColumn>
    <S.CapitalizedColumn
      data-testid={`DeliveryNoteDetailPickingStatus-${index + 1}`}
    >
      {capitalize(data.picking_status)}
    </S.CapitalizedColumn>
    <TStyle.TableColumn
      data-testid={`DeliveryNoteDetailDigitalSigned-${index + 1}`}
    >
      {data.is_digital_signed ? 'Elektronik' : 'Manual'}
    </TStyle.TableColumn>
    <TStyle.TableColumn
      data-testid={`DeliveryNoteDetailInvoiceAmount-${index + 1}`}
    >
      {formatCurrency(data.invoiced_amount)}
    </TStyle.TableColumn>
    <TStyle.TableColumn
      data-testid={`DeliveryNoteDetailKoliCount-${index + 1}`}
    >
      {data.koli_count} Box
    </TStyle.TableColumn>
  </TStyle.TableRow>
);

export const DeliveryNoteDetailDialog: React.FC<{
  open: boolean;
  deliveryNote: IDeliveryNote | null;
  onClose: () => void;
}> = ({ open, deliveryNote, onClose }) => {
  const statusColors = deliveryNote
    ? DELIVERY_NOTE_STATUS_BADGE_COLORS[deliveryNote.status] ?? {
        bg: '#E5E5E5',
        color: '#525252',
      }
    : { bg: '#E5E5E5', color: '#525252' };

  const statusText = deliveryNote
    ? DELIVERY_NOTE_STATUS_DISPLAY[deliveryNote.status] ?? deliveryNote.status
    : '-';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { maxWidth: '80vw', width: '80vw', maxHeight: '80vh' },
      }}
    >
      <S.DialogHeader container>
        <Grid item>
          <b>Delivery Note Detail</b>
        </Grid>
      </S.DialogHeader>

      <S.InfoRow container rowGap={2}>
        <Grid item xs={2}>
          <S.InfoLabel>AWB Number</S.InfoLabel>
          <S.InfoValue>{deliveryNote?.awb_number ?? '-'}</S.InfoValue>
        </Grid>
        <Grid item xs={2}>
          <S.InfoLabel>Status</S.InfoLabel>
          <S.StatusBadgeWrapper>
            <S.StatusBadge $bg={statusColors.bg} $color={statusColors.color}>
              {statusText}
            </S.StatusBadge>
          </S.StatusBadgeWrapper>
        </Grid>
        <Grid item xs={2}>
          <S.InfoLabel>Tanggal</S.InfoLabel>
          <S.InfoValue>
            {formatDate(deliveryNote?.ordered_at || '', 'dd MMMM yyyy')}
          </S.InfoValue>
        </Grid>
        <Grid item xs={2}>
          <S.InfoLabel>Tanggal Jatuh Tempo</S.InfoLabel>
          <S.InfoValue>
            {formatDate(deliveryNote?.due_date || '', 'dd MMMM yyyy')}
          </S.InfoValue>
        </Grid>
        <Grid item xs={2}>
          <S.InfoLabel>Delivery Type</S.InfoLabel>
          <S.InfoValue>
            {deliveryNote
              ? getDeliveryType(
                  deliveryNote.is_regular_delivery,
                  deliveryNote.is_coldchain_delivery,
                )
              : '-'}
          </S.InfoValue>
        </Grid>
      </S.InfoRow>

      <S.CustomerRow container rowGap={2}>
        <Grid item xs={2}>
          <S.InfoLabel>Customer ID</S.InfoLabel>
          <S.InfoValue>{deliveryNote?.pharmacy_id ?? '-'}</S.InfoValue>
        </Grid>
        <Grid item xs={2}>
          <S.InfoLabel>Nama Customer</S.InfoLabel>
          <S.InfoValue>{deliveryNote?.pharmacy_name ?? '-'}</S.InfoValue>
        </Grid>
        <Grid item xs={8}>
          <S.InfoLabel>Alamat Apotek</S.InfoLabel>
          <S.InfoValue>{deliveryNote?.pharmacy_address ?? '-'}</S.InfoValue>
        </Grid>
      </S.CustomerRow>

      <S.TableWrapper>
        <DataTable rowTitles={rowTitles} hideCheckbox>
          <TStyle.TableBody>
            {(deliveryNote?.invoices ?? []).map((invoice, index) => (
              <InvoiceRow
                key={`${invoice.po_number}-${index}`}
                data={invoice}
                index={index}
              />
            ))}
          </TStyle.TableBody>
        </DataTable>
      </S.TableWrapper>
    </Dialog>
  );
};
